import { useState, useEffect, useCallback } from "react";
import api, { apiErr, apiGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Archive, RotateCcw, History, LineChart, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { inputCls } from "./fields";
import { FieldHelp, HelpCtx, useHelpForm } from "./fields";

// Approved contextual-help copy (examples only — K9 imposes no methodology).
const H = (title, copy) => (<><b>{title}</b><br />{copy}</>);
const HELP_MTYPE = (
  <>
    <b>Tipo di misurazione</b><br />
    Scegli come vuoi registrare questo indicatore nel tempo. Usa un formato che permetta di confrontare osservazioni successive in modo coerente.
    <br /><b>Numero</b>: per quantità o conteggi. Es.: numero di episodi osservati.
    <br /><b>Distanza</b>: per misurare una distanza. Es.: distanza dal trigger.
    <br /><b>Durata</b>: per misurare un intervallo di tempo. Es.: tempo necessario per recuperare.
    <br /><b>Scala numerica</b>: per valutazioni su una scala definita da te. Es.: intensità della risposta da 0 a 5.
    <br /><b>Categoria</b>: per scegliere ogni volta tra categorie definite da te. Es.: Calmo / Attivato / Reattivo.
    <br /><b>Sì / No</b>: per registrare la presenza o l'assenza di una condizione osservabile.
    <br /><b>Testo breve</b>: per osservazioni sintetiche che non è utile esprimere con un numero, una scala o categorie.
  </>
);
const HELP_UNIT = H("Unità", "Definisce l'unità con cui verranno registrati e confrontati i valori di questo indicatore. Scegli quella che userai in modo coerente nelle osservazioni successive.");
const HELP_SCALE = H("Limiti della scala", "Definisci gli estremi della tua scala. Per esempio 0–5 oppure 1–10. Una volta registrate osservazioni, la struttura dell'indicatore viene mantenuta per preservare la coerenza dello storico.");
const HELP_CATS = H("Categorie", "Definisci le opzioni tra cui scegliere durante ogni osservazione. Usa categorie chiare e coerenti nel tempo, per esempio Calmo / Attivato / Reattivo.");
const HELP_DIR = H("Interpretazione dell'andamento", "Indica solo se, per questo indicatore, valori più alti o più bassi rappresentano un progresso. Se il dato serve semplicemente a descrivere ciò che osservi, lascia 'Nessuna interpretazione'.");
const HELP_NOTE = H("Nota di contesto", "Annota le condizioni utili a interpretare e confrontare questa osservazione con le successive, ad esempio tipo di trigger, ambiente, movimento, presenza di altri cani o altre circostanze rilevanti.");
const HELP_TEMPLATES = H("Modelli rapidi", "Sono scorciatoie modificabili per creare più velocemente un indicatore. Non sono valutazioni o protocolli imposti da K9: puoi modificarli oppure creare un indicatore da zero.");

const TYPE_LABELS = {
  number: "Numero", distance: "Distanza", duration: "Durata", numeric_scale: "Scala numerica",
  category: "Categoria", boolean: "Sì / No", short_text: "Testo breve",
};
const TYPE_ORDER = ["number", "distance", "duration", "numeric_scale", "category", "boolean", "short_text"];
const DIST_UNITS = [["m", "metri (m)"], ["cm", "centimetri (cm)"]];
const DUR_UNITS = [["s", "secondi (s)"], ["min", "minuti (min)"]];
const UNIT_SHORT = { m: "m", cm: "cm", s: "s", min: "min" };
const DIR_LABELS = {
  higher_is_progress: "Più alto = progresso", lower_is_progress: "Più basso = progresso", neutral: "Nessuna interpretazione",
};
const DIR_OPTS = ["neutral", "higher_is_progress", "lower_is_progress"];

// Static starter templates — pure frontend prefill, never authoritative methodology.
const TEMPLATES = [
  { label: "Distanza dal trigger", name: "Distanza dal trigger", measurement_type: "distance", config: { unit: "m" }, progress_direction: "neutral" },
  { label: "Tempo di recupero", name: "Tempo di recupero", measurement_type: "duration", config: { unit: "s" }, progress_direction: "neutral" },
  { label: "Frequenza episodi", name: "Frequenza episodi", measurement_type: "number", config: { unit: "episodi" }, progress_direction: "neutral" },
  { label: "Durata comportamento", name: "Durata comportamento", measurement_type: "duration", config: { unit: "s" }, progress_direction: "neutral" },
  { label: "Intensità risposta", name: "Intensità risposta", measurement_type: "numeric_scale", config: { min: 0, max: 10 }, progress_direction: "neutral" },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const numStr = (n) => (typeof n === "number" ? String(Number(n)) : String(n ?? ""));

const fmtValue = (ind, snap) => {
  if (!snap) return null;
  const v = snap.value;
  const cfg = ind.config || {};
  switch (ind.measurement_type) {
    case "number": return cfg.unit ? `${numStr(v)} ${cfg.unit}` : numStr(v);
    case "distance":
    case "duration": return `${numStr(v)} ${UNIT_SHORT[cfg.unit] || ""}`.trim();
    case "numeric_scale": return `${numStr(v)} / ${numStr(cfg.max)}`;
    case "category": return String(v);
    case "boolean": return v ? "Sì" : "No";
    case "short_text": return String(v);
    default: return String(v);
  }
};

// ---------------------------------------------------------------------------
// Indicator create/edit dialog
// ---------------------------------------------------------------------------
const IndicatorDialog = ({ dogId, indicator, onClose, onSaved }) => {
  const editing = !!indicator;
  const locked = editing && (indicator.snapshot_count || 0) > 0; // structural change blocked
  const [name, setName] = useState(indicator?.name || "");
  const [description, setDescription] = useState(indicator?.description || "");
  const [mtype, setMtype] = useState(indicator?.measurement_type || "number");
  const [unit, setUnit] = useState(indicator?.config?.unit ?? (mtype === "distance" ? "m" : mtype === "duration" ? "s" : ""));
  const [smin, setSmin] = useState(indicator?.config?.min ?? 0);
  const [smax, setSmax] = useState(indicator?.config?.max ?? 10);
  const [options, setOptions] = useState(indicator?.config?.options ? [...indicator.config.options] : ["", ""]);
  const [direction, setDirection] = useState(indicator?.progress_direction || "neutral");
  const [saving, setSaving] = useState(false);

  const applyTemplate = (t) => {
    setName(t.name); setMtype(t.measurement_type); setDirection(t.progress_direction);
    setUnit(t.config.unit ?? ""); if (t.config.min != null) setSmin(t.config.min); if (t.config.max != null) setSmax(t.config.max);
  };
  const onType = (v) => {
    setMtype(v);
    if (v === "distance") setUnit("m"); else if (v === "duration") setUnit("s"); else if (v === "number") setUnit("");
  };
  const setOpt = (i, v) => setOptions((o) => o.map((x, j) => (j === i ? v : x)));
  const addOpt = () => setOptions((o) => [...o, ""]);
  const delOpt = (i) => setOptions((o) => o.filter((_, j) => j !== i));

  const buildConfig = () => {
    if (mtype === "number") return { unit: unit.trim() };
    if (mtype === "distance" || mtype === "duration") return { unit };
    if (mtype === "numeric_scale") return { min: Number(smin), max: Number(smax) };
    if (mtype === "category") return { options: options.map((o) => o.trim()).filter(Boolean) };
    return {};
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome."); return; }
    if (mtype === "numeric_scale" && !(Number(smin) < Number(smax))) { toast.error("Il minimo deve essere inferiore al massimo."); return; }
    if (mtype === "category") {
      const clean = options.map((o) => o.trim()).filter(Boolean);
      if (clean.length < 2) { toast.error("Servono almeno 2 categorie."); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        const body = { name: name.trim(), description: description.trim(), progress_direction: direction, record_version: indicator.record_version };
        if (!locked) { body.measurement_type = mtype; body.config = buildConfig(); }
        await api.put(`/monitoring-indicators/${indicator.id}`, body);
        toast.success("Indicatore aggiornato");
      } else {
        await api.post(`/dogs/${dogId}/monitoring-indicators`, {
          name: name.trim(), description: description.trim(), measurement_type: mtype, config: buildConfig(), progress_direction: direction,
        });
        toast.success("Indicatore creato");
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const directionRelevant = mtype !== "short_text" && mtype !== "boolean" && mtype !== "category";
  const help = useHelpForm();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="indicator-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifica indicatore" : "Nuovo indicatore"}</DialogTitle></DialogHeader>
        <HelpCtx.Provider value={help}>
        <div className="space-y-4">
          {!editing && (
            <div>
              <div className="flex flex-wrap items-center text-xs text-muted-foreground mb-1.5">Modelli rapidi (opzionali)<FieldHelp fkey="mon-templates" help={HELP_TEMPLATES} /></div>
              <div className="flex flex-wrap gap-1.5" data-testid="indicator-templates">
                {TEMPLATES.map((t) => (
                  <button key={t.label} type="button" data-testid={`template-${t.measurement_type}`} onClick={() => applyTemplate(t)}
                    className="text-xs rounded-full px-3 py-1 border border-input bg-background text-muted-foreground hover:border-primary">{t.label}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Nome</label>
            <input data-testid="indicator-name" className={`${inputCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Distanza dal trigger" />
          </div>
          <div>
            <label className="text-sm font-medium">Descrizione <span className="text-muted-foreground font-normal">(facoltativa)</span></label>
            <textarea data-testid="indicator-desc" className={`${inputCls} mt-1 resize-none`} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <div className="flex flex-wrap items-center text-sm font-medium">Tipo di misurazione<FieldHelp fkey="mon-mtype" help={HELP_MTYPE} /></div>
            <select data-testid="indicator-type" disabled={locked} className={`${inputCls} mt-1 disabled:opacity-60`} value={mtype} onChange={(e) => onType(e.target.value)}>
              {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
            {locked && <div className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="indicator-locked-note"><AlertTriangle className="h-3.5 w-3.5" />La struttura non è modificabile: l'indicatore ha osservazioni registrate.</div>}
          </div>

          {/* type-specific configuration */}
          {mtype === "number" && (
            <div><div className="flex flex-wrap items-center text-sm font-medium">Unità <span className="text-muted-foreground font-normal ml-1">(facoltativa)</span><FieldHelp fkey="mon-unit" help={HELP_UNIT} /></div>
              <input data-testid="indicator-unit" disabled={locked} className={`${inputCls} mt-1 disabled:opacity-60`} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Es. episodi" /></div>
          )}
          {(mtype === "distance" || mtype === "duration") && (
            <div><div className="flex flex-wrap items-center text-sm font-medium">Unità<FieldHelp fkey="mon-unit" help={HELP_UNIT} /></div>
              <select data-testid="indicator-unit" disabled={locked} className={`${inputCls} mt-1 disabled:opacity-60`} value={unit} onChange={(e) => setUnit(e.target.value)}>
                {(mtype === "distance" ? DIST_UNITS : DUR_UNITS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          )}
          {mtype === "numeric_scale" && (
            <div>
              <div className="flex flex-wrap items-center text-sm font-medium mb-1">Limiti della scala<FieldHelp fkey="mon-scale" help={HELP_SCALE} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium">Minimo</label><input data-testid="indicator-min" type="number" disabled={locked} className={`${inputCls} mt-1 disabled:opacity-60`} value={smin} onChange={(e) => setSmin(e.target.value)} /></div>
                <div><label className="text-sm font-medium">Massimo</label><input data-testid="indicator-max" type="number" disabled={locked} className={`${inputCls} mt-1 disabled:opacity-60`} value={smax} onChange={(e) => setSmax(e.target.value)} /></div>
              </div>
            </div>
          )}
          {mtype === "category" && (
            <div>
              <div className="flex flex-wrap items-center text-sm font-medium">Categorie <span className="text-muted-foreground font-normal ml-1">(min. 2)</span><FieldHelp fkey="mon-cats" help={HELP_CATS} /></div>
              <div className="space-y-2 mt-1" data-testid="indicator-options">
                {options.map((o, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input data-testid={`indicator-option-${i}`} disabled={locked} className={`${inputCls} disabled:opacity-60`} value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Opzione ${i + 1}`} />
                    {!locked && options.length > 2 && <button type="button" data-testid={`indicator-option-del-${i}`} onClick={() => delOpt(i)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
              {!locked && <button type="button" data-testid="indicator-option-add" onClick={addOpt} className="text-sm text-primary font-medium mt-2 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Aggiungi opzione</button>}
            </div>
          )}

          {directionRelevant && (
            <div><div className="flex flex-wrap items-center text-sm font-medium">Interpretazione dell'andamento <span className="text-muted-foreground font-normal ml-1">(facoltativa)</span><FieldHelp fkey="mon-dir" help={HELP_DIR} /></div>
              <select data-testid="indicator-direction" className={`${inputCls} mt-1`} value={direction} onChange={(e) => setDirection(e.target.value)}>
                {DIR_OPTS.map((d) => <option key={d} value={d}>{DIR_LABELS[d]}</option>)}
              </select></div>
          )}
        </div>
        </HelpCtx.Provider>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="indicator-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="indicator-save">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Snapshot value control (adapts to measurement type)
// ---------------------------------------------------------------------------
const ValueControl = ({ ind, value, onChange }) => {
  const cfg = ind.config || {};
  if (ind.measurement_type === "boolean") {
    return (
      <div className="flex gap-2" data-testid="snapshot-value">
        {[["true", "Sì"], ["false", "No"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`snapshot-bool-${v}`} onClick={() => onChange(v)}
            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${String(value) === v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input"}`}>{l}</button>
        ))}
      </div>
    );
  }
  if (ind.measurement_type === "category") {
    return (
      <select data-testid="snapshot-value" className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>Seleziona…</option>
        {(cfg.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (ind.measurement_type === "short_text") {
    return <textarea data-testid="snapshot-value" className={`${inputCls} resize-none`} rows={3} maxLength={500} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  // number / distance / duration / numeric_scale
  const suffix = ind.measurement_type === "numeric_scale" ? `${numStr(cfg.min)}–${numStr(cfg.max)}` : (UNIT_SHORT[cfg.unit] || cfg.unit || "");
  return (
    <div className="flex items-center gap-2">
      <input data-testid="snapshot-value" type="number" inputMode="decimal" className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        min={ind.measurement_type === "numeric_scale" ? cfg.min : undefined} max={ind.measurement_type === "numeric_scale" ? cfg.max : undefined} />
      {suffix && <span className="text-sm text-muted-foreground whitespace-nowrap">{suffix}</span>}
    </div>
  );
};

// Snapshot create / correct dialog
const SnapshotDialog = ({ ind, snapshot, onClose, onSaved }) => {
  const correcting = !!snapshot;
  const [date, setDate] = useState(snapshot?.observation_date || todayISO());
  const [value, setValue] = useState(() => {
    if (!snapshot) return ind.measurement_type === "boolean" ? "true" : "";
    return ind.measurement_type === "boolean" ? String(snapshot.value) : snapshot.value;
  });
  const [note, setNote] = useState(snapshot?.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (value === "" || value == null) { toast.error("Inserisci un valore."); return; }
    setSaving(true);
    let payloadValue = value;
    if (ind.measurement_type === "boolean") payloadValue = value === "true";
    try {
      if (correcting) {
        await api.put(`/monitoring-snapshots/${snapshot.id}`, { observation_date: date, value: payloadValue, note: note.trim(), record_version: snapshot.record_version });
        toast.success("Osservazione corretta");
      } else {
        await api.post(`/monitoring-indicators/${ind.id}/snapshots`, { observation_date: date, value: payloadValue, note: note.trim() });
        toast.success("Osservazione aggiunta");
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const help = useHelpForm();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="snapshot-dialog">
        <DialogHeader><DialogTitle>{correcting ? "Correggi osservazione" : "Nuova osservazione"} · {ind.name}</DialogTitle></DialogHeader>
        <HelpCtx.Provider value={help}>
        <div className="space-y-4">
          <div><label className="text-sm font-medium">Data</label>
            <input data-testid="snapshot-date" type="date" className={`${inputCls} mt-1`} value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><label className="text-sm font-medium">Valore</label><div className="mt-1"><ValueControl ind={ind} value={value} onChange={setValue} /></div></div>
          <div><div className="flex flex-wrap items-center text-sm font-medium">Nota di contesto <span className="text-muted-foreground font-normal ml-1">(facoltativa)</span><FieldHelp fkey="mon-note" help={HELP_NOTE} /></div>
            <textarea data-testid="snapshot-note" className={`${inputCls} mt-1 resize-none`} rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          {correcting && <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />La correzione viene registrata nel registro attività.</div>}
        </div>
        </HelpCtx.Provider>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="snapshot-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="snapshot-save">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// History dialog
const HistoryDialog = ({ ind, onClose, onChanged }) => {
  const [snaps, setSnaps] = useState(null);
  const [error, setError] = useState(false);
  const [correct, setCorrect] = useState(null);
  const load = useCallback(async () => {
    setError(false);
    try { const { data } = await apiGet(`/monitoring-indicators/${ind.id}/snapshots`); setSnaps(data || []); }
    catch (e) { setError(true); }
  }, [ind.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Storico · {ind.name}</DialogTitle></DialogHeader>
        {error && <div className="text-sm text-destructive py-6 text-center" data-testid="history-error">Impossibile caricare lo storico. <button onClick={load} className="underline font-medium">Riprova</button></div>}
        {!error && snaps === null && <div className="text-sm text-muted-foreground py-6 text-center">Caricamento…</div>}
        {!error && snaps && snaps.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="history-empty">Nessuna osservazione registrata.</div>}
        {!error && snaps && snaps.length > 0 && (
          <div className="space-y-2">
            {snaps.map((s) => (
              <div key={s.id} data-testid={`snapshot-row-${s.id}`} className="rounded-xl border border-border p-3 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{fmtValue(ind, s)}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(s.observation_date)}</span>
                    {s.corrected_at && <span className="text-[10px] uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5">corretta</span>}
                  </div>
                  {s.note && <div className="text-sm text-muted-foreground mt-1 break-words">{s.note}</div>}
                </div>
                <button type="button" data-testid={`snapshot-correct-${s.id}`} onClick={() => setCorrect(s)} className="text-muted-foreground hover:text-primary shrink-0" title="Correggi"><Pencil className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
        {correct && <SnapshotDialog ind={ind} snapshot={correct} onClose={() => setCorrect(null)} onSaved={() => { setCorrect(null); load(); onChanged(); }} />}
      </DialogContent>
    </Dialog>
  );
};

// Indicator card
const IndicatorCard = ({ ind, onAddSnap, onHistory, onEdit, onArchive, onRestore }) => {
  const archived = !!ind.archived_at;
  return (
    <div data-testid={`indicator-card-${ind.id}`} className={`rounded-2xl border border-border bg-card p-4 ${archived ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-head font-bold text-base truncate">{ind.name}</h3>
            <span className="text-[11px] rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">{TYPE_LABELS[ind.measurement_type]}</span>
            {archived && <span className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">Archiviato</span>}
          </div>
          {ind.description && <p className="text-sm text-muted-foreground mt-0.5 break-words">{ind.description}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" data-testid={`indicator-edit-${ind.id}`} onClick={() => onEdit(ind)} className="text-muted-foreground hover:text-primary p-1" title="Modifica"><Pencil className="h-4 w-4" /></button>
          {archived
            ? <button type="button" data-testid={`indicator-restore-${ind.id}`} onClick={() => onRestore(ind)} className="text-muted-foreground hover:text-primary p-1" title="Ripristina"><RotateCcw className="h-4 w-4" /></button>
            : <button type="button" data-testid={`indicator-archive-${ind.id}`} onClick={() => onArchive(ind)} className="text-muted-foreground hover:text-destructive p-1" title="Archivia"><Archive className="h-4 w-4" /></button>}
        </div>
      </div>

      <div className="mt-3 flex items-end gap-4">
        <div>
          <div className="text-xs text-muted-foreground">Ultimo valore</div>
          <div className="text-xl font-extrabold" data-testid={`indicator-latest-${ind.id}`}>{ind.latest ? fmtValue(ind, ind.latest) : "—"}</div>
          {ind.latest && <div className="text-xs text-muted-foreground">{fmtDate(ind.latest.observation_date)}</div>}
        </div>
        {ind.previous && (
          <div className="pb-0.5">
            <div className="text-xs text-muted-foreground">Precedente</div>
            <div className="text-sm font-semibold text-muted-foreground" data-testid={`indicator-previous-${ind.id}`}>{fmtValue(ind, ind.previous)}</div>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        {!archived && <Button size="sm" onClick={() => onAddSnap(ind)} data-testid={`indicator-add-snapshot-${ind.id}`}><Plus className="h-4 w-4 mr-1" />Osservazione</Button>}
        <Button size="sm" variant="outline" onClick={() => onHistory(ind)} data-testid={`indicator-history-${ind.id}`}><History className="h-4 w-4 mr-1" />Storico{ind.snapshot_count ? ` (${ind.snapshot_count})` : ""}</Button>
      </div>
    </div>
  );
};

// Main tab
export const MonitoringTab = ({ dogId }) => {
  const [indicators, setIndicators] = useState(null);
  const [error, setError] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState(null);     // {mode:'create'|'edit', indicator}
  const [snapFor, setSnapFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    // Always fetch archived too, so the UI can expose restore even when 0 active remain.
    try { const { data } = await apiGet(`/dogs/${dogId}/monitoring-indicators?include_archived=true`); setIndicators(data || []); }
    catch (e) { setError(true); }
  }, [dogId]);
  useEffect(() => { load(); }, [load]);

  const archive = async (ind) => { try { await api.post(`/monitoring-indicators/${ind.id}/archive`); toast.success("Indicatore archiviato"); setConfirmArchive(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const restore = async (ind) => { try { await api.post(`/monitoring-indicators/${ind.id}/restore`); toast.success("Indicatore ripristinato"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const active = (indicators || []).filter((i) => !i.archived_at);
  const archivedList = (indicators || []).filter((i) => i.archived_at);
  const firstUse = indicators && indicators.length === 0; // STATE A: nothing ever created

  return (
    <div className="space-y-4" data-testid="monitoring-tab">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-head text-lg font-bold flex items-center gap-2"><LineChart className="h-5 w-5 text-primary" />Monitoraggio</h2>
          <p className="text-sm text-muted-foreground">Indicatori personalizzati e osservazioni nel tempo.</p>
        </div>
        {indicators && indicators.length > 0 && <Button size="sm" onClick={() => setDialog({ mode: "create" })} data-testid="monitoring-add-indicator"><Plus className="h-4 w-4 mr-1" />Indicatore</Button>}
      </div>

      {error && <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="monitoring-error">
        <p className="text-sm text-destructive">Impossibile caricare il monitoraggio.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load}>Riprova</Button></div>}

      {!error && indicators === null && <div className="text-sm text-muted-foreground py-10 text-center">Caricamento…</div>}

      {/* STATE A — true first use */}
      {!error && firstUse && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="monitoring-empty">
          <LineChart className="h-10 w-10 text-primary/40 mx-auto" />
          <h3 className="font-head font-bold mt-3">Monitora l'evoluzione di questo cane</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Crea indicatori personalizzati — distanza dal trigger, tempo di recupero, frequenza episodi… — e registra osservazioni datate per seguirne l'andamento.</p>
          <Button className="mt-4" onClick={() => setDialog({ mode: "create" })} data-testid="monitoring-create-first"><Plus className="h-4 w-4 mr-1" />Crea il primo indicatore</Button>
        </div>
      )}

      {/* STATE B — no active indicators but archived ones exist */}
      {!error && !firstUse && active.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="monitoring-no-active">
          <p className="text-sm text-muted-foreground">Nessun indicatore attivo. Gli indicatori archiviati sono conservati con il loro storico.</p>
          <Button className="mt-3" size="sm" onClick={() => setDialog({ mode: "create" })} data-testid="monitoring-create-another"><Plus className="h-4 w-4 mr-1" />Nuovo indicatore</Button>
        </div>
      )}

      {/* STATE C — active list */}
      {!error && active.length > 0 && <div className="space-y-3">
        {active.map((ind) => (
          <IndicatorCard key={ind.id} ind={ind} onAddSnap={setSnapFor} onHistory={setHistFor}
            onEdit={(i) => setDialog({ mode: "edit", indicator: i })} onArchive={setConfirmArchive} onRestore={restore} />
        ))}
      </div>}

      {/* Archived access — always reachable whenever archived indicators exist */}
      {!error && archivedList.length > 0 && (
        <div className="pt-2">
          <button type="button" data-testid="monitoring-show-archived" onClick={() => setShowArchived((v) => !v)} className="text-sm text-muted-foreground hover:text-foreground underline">
            {showArchived ? "Nascondi archiviati" : `Indicatori archiviati (${archivedList.length})`}
          </button>
          {showArchived && <div className="space-y-3 mt-3">
            {archivedList.map((ind) => (
              <IndicatorCard key={ind.id} ind={ind} onAddSnap={setSnapFor} onHistory={setHistFor}
                onEdit={(i) => setDialog({ mode: "edit", indicator: i })} onArchive={setConfirmArchive} onRestore={restore} />
            ))}
          </div>}
        </div>
      )}

      <AlertDialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archivia indicatore?</AlertDialogTitle>
            <AlertDialogDescription>
              L'indicatore uscirà dalla lista attiva. Le osservazioni e lo storico NON verranno eliminati e potrai ripristinarlo in qualsiasi momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="archive-confirm" onClick={() => confirmArchive && archive(confirmArchive)}>Archivia</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dialog && <IndicatorDialog dogId={dogId} indicator={dialog.indicator} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
      {snapFor && <SnapshotDialog ind={snapFor} onClose={() => setSnapFor(null)} onSaved={() => { setSnapFor(null); load(); }} />}
      {histFor && <HistoryDialog ind={histFor} onClose={() => setHistFor(null)} onChanged={load} />}
    </div>
  );
};
